/**
 * "The request path" (docs section 7): the five stations of one API call as a Timeline
 * with arrows. The browser, nginx in the frontend container, the session check and the
 * mailbox scope in the API, then the two databases. Each station shows the code name to
 * search for. Under the stations: the four status codes that stop a call. Horizontal on a
 * laptop, vertical on a phone (the narrow media query), as "How I2Q works".
 */

import {
  Database,
  Funnel,
  Icon,
  type IconComponent,
  Monitor,
  Server,
  ShieldCheck,
} from "@norican/icons";
import {
  Badge,
  cn,
  Timeline,
  type TimelineItem,
  type TimelineTone,
  Typography,
} from "@norican/react";
import type { ReactNode } from "react";

import { NARROW_QUERY, useMediaQuery } from "@/hooks/use-media-query";

import { Figure } from "./figure";

export const REQUEST_PATH_TITLE = "The request path";

/** A station: what it does in one or two short sentences, and the code name to search for. */
interface Station {
  id: string;
  icon: IconComponent;
  title: string;
  text: string;
  name: string;
  tone: TimelineTone;
}

/** The five stations in the order a call passes them. */
export const REQUEST_STATIONS: readonly Station[] = [
  {
    id: "browser",
    icon: Monitor,
    title: "Browser",
    text: "Every call goes to the host of the page.",
    name: "/api/*",
    tone: "default",
  },
  {
    id: "nginx",
    icon: Server,
    title: "nginx in the frontend",
    text: "It cuts `/api` and proxies to the backend. Other paths get the app.",
    name: "nginx.conf.template",
    tone: "default",
  },
  {
    id: "session",
    icon: ShieldCheck,
    title: "API: the session",
    text: "It checks the signed cookie `boi-auth-session`.",
    name: "require_session",
    tone: "accent",
  },
  {
    id: "scope",
    icon: Funnel,
    title: "API: the mailbox scope",
    text: "The areas in the cookie give the mailboxes the person sees.",
    name: "require_mailbox_scope",
    tone: "accent",
  },
  {
    id: "stores",
    icon: Database,
    title: "The two databases",
    text: "App DB: read and write. Mirror: read only, as `rfq_reader`.",
    name: "rfq_foundry/store/",
    tone: "success",
  },
];

export const REQUEST_STOPS_HEADING = "Where a call stops";

/**
 * The status codes that end a call before the data. The API checks the scope before the
 * capability on most endpoints (`api/main.py`: `_require_rules_entity` gives the 404 for an
 * entity, `_require_engine_area` gives the 403 for an on-demand call).
 */
export const REQUEST_STOPS: readonly (readonly [string, string])[] = [
  ["401", "The session cookie is missing or bad."],
  ["404", "The case, or the entity of a rules call, is outside the scope."],
  [
    "403",
    "The role of the person does not give the capability. An on-demand call for an entity outside the areas of the session also gets 403.",
  ],
  ["503", "The API has no configuration for the app DB."],
];

export const REQUEST_PATH_NOTE =
  "The backend has internal ingress only. In local development, Vite does the work of nginx.";

const TEXT = "flex min-w-0 flex-col items-start gap-1";
/** The code name under a station: it wraps inside the station and never runs into the next. */
const NAME = "max-w-full font-mono break-all whitespace-normal";

/** Backtick spans in code font, as the documentation page prints them. */
function code(text: string): ReactNode {
  return text.split("`").map((part, index) =>
    index % 2 ? (
      <Typography key={index} as="code" variant="code" className="text-[0.9em] break-words">
        {part}
      </Typography>
    ) : (
      part
    ),
  );
}

export interface RequestPathFigureProps {
  caption?: string;
  /** Overrides the media query for the render tests. Omit it in the app. */
  orientation?: "horizontal" | "vertical";
}

export function RequestPathFigure({ caption, orientation }: RequestPathFigureProps) {
  const narrow = useMediaQuery(NARROW_QUERY);
  const form = orientation ?? (narrow ? "vertical" : "horizontal");
  const items: TimelineItem[] = REQUEST_STATIONS.map((station) => ({
    id: station.id,
    title: station.title,
    tone: station.tone,
    icon: <Icon icon={station.icon} size="sm" />,
    // DS-GAP(timeline-horizontal-last-item): the last item of a horizontal Timeline does
    // not share the width (it takes the width of its text and squeezes the others), so
    // the text of every station is capped to the same width in the horizontal form.
    description: (
      <span className={cn(TEXT, form === "horizontal" && "max-w-36")}>
        <span className="break-words">{code(station.text)}</span>
        <Badge tone="neutral" variant="outline" size="sm" shape="rounded" className={NAME}>
          {station.name}
        </Badge>
      </span>
    ),
  }));
  return (
    <Figure name="request-path" title={REQUEST_PATH_TITLE} caption={caption}>
      {/* DS-GAP(timeline-horizontal-wrap): the horizontal Timeline does not wrap, so the
          figure switches to the vertical form below 768 px by media query. */}
      <Timeline
        orientation={form}
        connector="arrow"
        items={items}
        aria-label="The five stations of an API call, from the browser to the two databases"
      />
      <Typography variant="label">{REQUEST_STOPS_HEADING}</Typography>
      <ul
        aria-label={REQUEST_STOPS_HEADING}
        className={form === "vertical" ? "flex flex-col gap-1" : "grid grid-cols-2 gap-x-4 gap-y-1"}
      >
        {REQUEST_STOPS.map(([status, text]) => (
          <li key={status} className="flex min-w-0 items-start gap-1.5 text-sm">
            <Badge tone="warning" variant="subtle" size="sm" className="tabular-nums">
              {status}
            </Badge>
            <span className="min-w-0 break-words">{text}</span>
          </li>
        ))}
      </ul>
      <Typography variant="caption" tone="muted" className="break-words">
        {REQUEST_PATH_NOTE}
      </Typography>
    </Figure>
  );
}
